import {
  createAsyncThunk,
  createSlice,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import {
  fetchRawMaterialsByWarehouseApi,
  createStoreInwardApi,
  fetchDropdownDataApi,
  fetchStoreApprovedRequestsApi,
  fulfillStoreRequestApi,
  LowStockRawMaterialsListApi,
  fetchStoreStockAdminViewPaginationApi,
  downloadStoreStockAdminReportDownloadApi,
  fetchStorePriceByWarehouseApi,
  createMaterialOutwardApi,
  fetchPendingOutwardRequestsApi,
  submitMaterialInwardFromOutwardApi,
  updateStoreInwardApi,
} from "../../api/warehouseStoreInward.api";
import { IWarehouseRecord } from "../../models/warehouse.model";
import {
  IWarehouseRawMaterial,
  IWarehouseRawMaterialResponse,
} from "../../models/warehouseRawMaterial.model";
import { IVendor } from "../../models/vendor.model";

export interface StorePendingRequest {
  _id: string;
  productId: {
    _id: string;
    name: string;
    sku?: string;
    unit: string;
    unitType: string;
  };
  requestedQuantity: number;
  unit: string;
  userId: { username: string } | string;
  createdAt: string;
  status: string;
  history: { remark?: string; userId?: { username: string } }[];
}

export interface FulfillRequest {
  id: string;
  quantity: number;
  unit: string;
  remark?: string;
}

interface WarehouseRawMaterials {
  warehouseId: string;
  warehouseName: string;
  warehouse: IWarehouseRecord;
  rawMaterials: IWarehouseRawMaterialResponse[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface StoreStockResponseItem {
  _id: string;
  name: string;
  sku: string;
  stock: {
    warehouse: { _id: string; name: string };
    totalPrice: number;
    quantity: { value: number; unit: string };
  }[];
}

interface StoreState {
  pendingOutwardRequests: any[];
  rawMaterialsByWarehouse: { [warehouseId: string]: WarehouseRawMaterials };
  lowStock: {
    rawMaterials: IWarehouseRawMaterialResponse[];
    pagination: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
    };
    search: string;
  };
  vendors: IVendor[];
  warehouses: IWarehouseRecord[];
  rawMaterials: IWarehouseRawMaterial[];
  pendingRequests: {
    data: StorePendingRequest[];
    pagination: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
    };
    search: string;
  };
  storeStock: {
    rawMaterials: StoreStockResponseItem[];
    pagination: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      limit: number;
    };
    search: string;
    loading: boolean;
    isExporting: boolean;
  };
  warehouseValues: {
    warehouses: {
      warehouseId: string;
      name: string;
      totalPrice: number;
    }[];
    grandTotal: number;
    loading: boolean;
  };
  loading: boolean;
  error: string | null;
}

const initialState: StoreState = {
  pendingOutwardRequests: [],
  rawMaterialsByWarehouse: {},
  lowStock: {
    rawMaterials: [],
    pagination: { totalItems: 0, totalPages: 0, currentPage: 1 },
    search: "",
  },
  vendors: [],
  warehouses: [],
  rawMaterials: [],
  pendingRequests: {
    data: [],
    pagination: { totalItems: 0, totalPages: 0, currentPage: 1 },
    search: "",
  },
  storeStock: {
    rawMaterials: [],
    pagination: { totalItems: 0, totalPages: 0, currentPage: 1, limit: 10 },
    search: "",
    loading: false,
    isExporting: false,
  },
  warehouseValues: {
    warehouses: [],
    grandTotal: 0,
    loading: false,
  },
  loading: false,
  error: null,
};

// export const fetchRawMaterialsByWarehouse = createAsyncThunk(
//   "store/fetchRawMaterialsByWarehouse",
//   async (
//     {
//       page = 1,
//       limit = 10,
//       search = "",
//     }: { page?: number; limit?: number; search?: string },
//     { rejectWithValue }
//   ) => {
//     try {
//       return await fetchRawMaterialsByWarehouseApi({ page, limit, search });
//     } catch (err: any) {
//       return rejectWithValue(
//         err.message || "Failed to fetch raw materials by warehouse"
//       );
//     }
//   }
// );

export const fetchPendingOutwardRequests = createAsyncThunk(
  "store/fetchPendingOutwardRequests",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchPendingOutwardRequestsApi();
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch pending outward requests",
      );
    }
  },
);

export const submitMaterialInwardFromOutward = createAsyncThunk(
  "store/submitMaterialInwardFromOutward",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await submitMaterialInwardFromOutwardApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Inward submission failed");
    }
  },
);

export const createMaterialOutward = createAsyncThunk(
  "store/createMaterialOutward",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await createMaterialOutwardApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Outward failed");
    }
  },
);

export const fetchRawMaterialsByWarehousePage = createAsyncThunk(
  "store/fetchRawMaterialsByWarehousePage",
  async (
    {
      warehouseId,
      page,
      limit,
      search = "",
    }: { warehouseId: string; page: number; limit: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      return await fetchRawMaterialsByWarehouseApi({
        warehouseId,
        page,
        limit,
        search,
      });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch raw materials for warehouse",
      );
    }
  },
);

export const fetchDropdownData = createAsyncThunk(
  "store/fetchDropdownData",
  async (usageType: string | undefined, { rejectWithValue }) => {
    try {
      const data = await fetchDropdownDataApi(usageType);
      return {
        vendors: data.vendors || [],
        warehouses: data.warehouses || [],
        rawMaterials: data.rawMaterials || [],
      };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch dropdown data");
    }
  },
);

export const createStoreInwardRequests = createAsyncThunk(
  "store/createStoreInward",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await createStoreInwardApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to create store inward");
    }
  },
);

export const fetchStoreApprovedRequests = createAsyncThunk(
  "store/fetchStoreApprovedRequests",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
    }: { page?: number; limit?: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      return await fetchStoreApprovedRequestsApi({ page, limit, search });
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  },
);

export const updateStoreInwardRequest = createAsyncThunk(
  "store/updateStoreInward",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await updateStoreInwardApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to update store inward");
    }
  },
);

export const fulfillStoreRequest = createAsyncThunk(
  "store/fulfillStoreRequest",
  async (payload: FulfillRequest[], { rejectWithValue }) => {
    try {
      return await fulfillStoreRequestApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message || "Error fulfilling requests");
    }
  },
);

export const fetchLowStockRawMaterials = createAsyncThunk(
  "store/fetchLowStockRawMaterials",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
    }: { page?: number; limit?: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      return await LowStockRawMaterialsListApi({ page, limit, search });
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch low-stock materials",
      );
    }
  },
);

export const fetchStoreStockAdminViewPagination = createAsyncThunk(
  "store/fetchStoreStockAdminViewPagination",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
    }: { page?: number; limit?: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      return await fetchStoreStockAdminViewPaginationApi({
        page,
        limit,
        search,
      });
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch store stock");
    }
  },
);

export const downloadStoreStockReport = createAsyncThunk(
  "store/downloadStoreStockReport",
  async (
    {
      format,
      search,
      date,
    }: { format: "xlsx" | "csv"; search: string; date: string },
    { rejectWithValue },
  ) => {
    try {
      const blob = await downloadStoreStockAdminReportDownloadApi({
        format,
        search,
        date,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `RawMaterialStock_${date}.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to download store stock report",
      );
    }
  },
);

export const getStorePriceByWarehouse = createAsyncThunk(
  "store/getStorePriceByWarehouse",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchStorePriceByWarehouseApi();
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch warehouse values",
      );
    }
  },
);

const storeSlice = createSlice({
  name: "store",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // .addCase(fetchRawMaterialsByWarehouse.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      // .addCase(fetchRawMaterialsByWarehouse.fulfilled, (state, action) => {
      //   const warehouses = Array.isArray(action.payload)
      //     ? action.payload
      //     : [action.payload];
      //   state.rawMaterialsByWarehouse = warehouses.reduce(
      //     (acc, warehouse) => ({
      //       ...acc,
      //       [warehouse.warehouseId]: warehouse,
      //     }),
      //     {}
      //   );
      //   state.loading = false;
      // })
      // .addCase(fetchRawMaterialsByWarehouse.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload as string;
      // })
      .addCase(fetchRawMaterialsByWarehousePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRawMaterialsByWarehousePage.fulfilled, (state, action) => {
        const warehouse = action.payload;
        state.rawMaterialsByWarehouse[warehouse.warehouseId] = {
          ...warehouse,
          rawMaterials: warehouse.rawMaterials,
          pagination: warehouse.pagination,
        };
        state.loading = false;
      })
      .addCase(fetchRawMaterialsByWarehousePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.vendors = action.payload.vendors;
        state.warehouses = action.payload.warehouses;
        state.rawMaterials = action.payload.rawMaterials;
        state.loading = false;
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createStoreInwardRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStoreInwardRequests.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createStoreInwardRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchStoreApprovedRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreApprovedRequests.fulfilled, (state, action) => {
        state.pendingRequests = {
          data: action.payload.data,
          pagination: action.payload.pagination,
          search: action.meta.arg.search || "",
        };
        state.loading = false;
      })
      .addCase(fetchStoreApprovedRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fulfillStoreRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fulfillStoreRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fulfillStoreRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchLowStockRawMaterials.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLowStockRawMaterials.fulfilled, (state, action) => {
        const { rawMaterials, pagination } = action.payload;
        state.lowStock = {
          rawMaterials,
          pagination,
          search: action.meta.arg.search || "",
        };
        state.loading = false;
      })
      .addCase(fetchLowStockRawMaterials.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchStoreStockAdminViewPagination.pending, (state) => {
        state.storeStock.loading = true;
        state.error = null;
      })
      .addCase(
        fetchStoreStockAdminViewPagination.fulfilled,
        (state, action) => {
          state.storeStock = {
            rawMaterials: action.payload.rawMaterials,
            pagination: {
              totalItems: action.payload.totalItems,
              totalPages: action.payload.totalPages,
              currentPage: action.payload.currentPage,
              limit: action.payload.limit,
            },
            search: action.meta.arg.search || "",
            loading: false,
            isExporting: false,
          };
        },
      )
      .addCase(fetchStoreStockAdminViewPagination.rejected, (state, action) => {
        state.storeStock.loading = false;
        state.error = action.payload as string;
      })
      .addCase(downloadStoreStockReport.pending, (state) => {
        state.storeStock.isExporting = true;
        state.error = null;
      })
      .addCase(downloadStoreStockReport.fulfilled, (state) => {
        state.storeStock.isExporting = false;
      })
      .addCase(downloadStoreStockReport.rejected, (state, action) => {
        state.storeStock.isExporting = false;
        state.error = action.payload as string;
      })
      .addCase(getStorePriceByWarehouse.pending, (state) => {
        state.warehouseValues.loading = true;
      })
      .addCase(getStorePriceByWarehouse.rejected, (state) => {
        state.warehouseValues.loading = false;
      })
      .addCase(getStorePriceByWarehouse.fulfilled, (state, action) => {
        state.warehouseValues = {
          warehouses: action.payload.warehouses,
          grandTotal: action.payload.grandTotal,
          loading: false,
        };
      })
      .addCase(createMaterialOutward.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMaterialOutward.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createMaterialOutward.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPendingOutwardRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingOutwardRequests.fulfilled, (state, action) => {
        state.pendingOutwardRequests = action.payload;
        state.loading = false;
      })
      .addCase(fetchPendingOutwardRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(submitMaterialInwardFromOutward.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitMaterialInwardFromOutward.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(submitMaterialInwardFromOutward.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateStoreInwardRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStoreInwardRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateStoreInwardRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess } = storeSlice.actions;
export default storeSlice.reducer;
